import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Botao,
  Carregando,
  Cartao,
  Etiqueta,
  Icone,
  paginar,
  Paginacao,
  Selo,
  Vazio,
} from '../../componentes/ui/index.ts';
import { api } from '../../lib/api.ts';
import { exportarXlsx } from '../../lib/exportar.ts';

type Linha = {
  id: string;
  protocolo: string;
  nomeAdolescente: string;
  house: string | null;
  pedidoQuarto: string | null;
  sexo: string | null;
  divideCom: string | null;
};

const SEM_HOUSE = 'Sem house definida';

export function Houses() {
  const [lista, setLista] = useState<Linha[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [pagina, setPagina] = useState(1);

  useEffect(() => {
    api<{ inscricoes: Linha[] }>('/api/admin/houses')
      .then((r) => setLista(r.inscricoes))
      .finally(() => setCarregando(false));
  }, []);

  if (carregando) return <Carregando />;

  // Agrupa por house, jogando os indefinidos num grupo próprio no fim.
  const grupos = new Map<string, Linha[]>();
  for (const l of lista) {
    const chave = l.house?.trim() || SEM_HOUSE;
    grupos.set(chave, [...(grupos.get(chave) ?? []), l]);
  }

  const ordenados = [...grupos.entries()].sort(([a], [b]) => {
    if (a === SEM_HOUSE) return 1;
    if (b === SEM_HOUSE) return -1;
    return a.localeCompare(b, 'pt-BR');
  });

  const visivel = paginar(ordenados, pagina);

  // A planilha leva todas as houses, não só a página que está na tela.
  function exportar() {
    exportarXlsx(
      'houses',
      lista.map((l) => ({
        Protocolo: l.protocolo,
        Adolescente: l.nomeAdolescente,
        House: l.house ?? SEM_HOUSE,
        Sexo: l.sexo ?? '',
        'Divide com': l.divideCom ?? l.pedidoQuarto ?? '',
      })),
    );
  }

  return (
    <div className="pilha-6">
      <div className="linha linha-entre">
        <div className="pilha-3">
          <Etiqueta>Quartos</Etiqueta>
          <h2>Organização por house</h2>
          <p className="texto-suave">A house de cada adolescente é definida na ficha dele.</p>
        </div>
        <Botao
          variante="secundario"
          tamanho="pequeno"
          disabled={lista.length === 0}
          onClick={exportar}
          icone={<Icone.Download tamanho={14} />}
        >
          Exportar planilha
        </Botao>
      </div>

      {lista.length === 0 ? (
        <Vazio titulo="Nenhuma inscrição enviada ainda">
          Os quartos aparecem aqui conforme as fichas chegam.
        </Vazio>
      ) : (
        <div className="grade" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
          {visivel.itens.map(([house, membros]) => (
            <Cartao
              key={house}
              marcado={house !== SEM_HOUSE}
              titulo={house}
              acao={<Selo tom={house === SEM_HOUSE ? 'alerta' : 'neutro'}>{membros.length}</Selo>}
            >
              <div className="pilha-3">
                {membros.map((m) => (
                  <div key={m.id} className="linha linha-entre linha-topo">
                    <div style={{ minWidth: 0 }}>
                      <Link to={`/painel/inscricoes/${m.id}`} style={{ fontWeight: 600 }}>
                        {m.nomeAdolescente}
                      </Link>
                      {m.sexo && <div className="texto-fraco">{m.sexo}</div>}
                      {m.divideCom && (
                        <div className="texto-fraco">divide com: {m.divideCom}</div>
                      )}
                      {/* Fichas antigas, anteriores à pergunta de divisão, só
                          têm o pedido em texto livre. */}
                      {!m.divideCom && m.pedidoQuarto && (
                        <div className="texto-fraco">pedido: {m.pedidoQuarto}</div>
                      )}
                    </div>
                    <span className="mono texto-fraco">{m.protocolo}</span>
                  </div>
                ))}
              </div>
            </Cartao>
          ))}
        </div>
      )}

      {/* Pagina as houses (cada cartão é uma house inteira), não os membros. */}
      <Paginacao total={ordenados.length} pagina={visivel.pagina} aoMudar={setPagina} />
    </div>
  );
}
